/**
 * Which colour each thing on the Overview charts is drawn in, and the rules
 * that keep it the same colour everywhere.
 *
 * The hex values live in platform/palette.ts, the one file allowed to hold
 * them, with the validation that chose them. What lives here is the mapping:
 * which outcome is which colour, which lane keeps which slot, and which theme
 * gets which set.
 *
 * The first version drew with Fluent's own saturated green, red and yellow
 * blocks, the loudest possible way to draw a chart that is mostly approvals.
 * Approvals are blue now and refusals are what stands out.
 */

import { overviewChartColours, type OverviewChartColours } from '../platform'
import { getThemeById } from '../themes/themeEngine'
import { useThemeChoice } from '../themeContext'

export type ChartPalette = OverviewChartColours

export function paletteFor(base: 'light' | 'dark'): ChartPalette {
  return overviewChartColours[base]
}

export function useChartPalette(): ChartPalette {
  const { themeId } = useThemeChoice()
  return paletteFor(getThemeById(themeId)?.base ?? 'dark')
}

// A lane keeps its colour whatever else is on screen. Colour follows the
// entity, never its position: were it assigned by order, a quiet range where
// TIM made no decisions would repaint every other lane, and a reader who had
// learned that DCS is orange would be misled.
const LANE_ORDER = ['tim', 'dcs', 'ocpi', 'hubject', 'default']

export function laneColour(palette: ChartPalette, lane: string, fallbackIndex: number): string {
  const known = LANE_ORDER.indexOf(lane)
  const slot = known >= 0 ? known : LANE_ORDER.length + fallbackIndex
  return palette.lanes[slot % palette.lanes.length]
}

export function outcomeColour(palette: ChartPalette, outcome: string): string {
  switch (outcome) {
    case 'APPROVED':
    case 'accepted':
      return palette.approved
    case 'BLOCKED':
    case 'rejected':
      return palette.blocked
    case 'failed':
      return palette.failed
    default:
      return palette.unknown
  }
}

/** Bottom of the stack first: the majority on the floor, the exceptions on top
 *  where the eye lands. */
export const OUTCOME_ORDER = ['APPROVED', 'UNKNOWN', 'BLOCKED']
export const SETTLEMENT_ORDER = ['accepted', 'incomplete', 'rejected', 'failed']

export function ordered(keys: string[], order: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ia = order.indexOf(a)
    const ib = order.indexOf(b)
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib) || a.localeCompare(b)
  })
}
